import { Userbot, UserbotCatalog } from "../../interfaces"

/** Match-all условие по умолчанию (TZ §3.9, §9.3). */
export const defaultCondition = (): Userbot.ConditionExpr => ({
  op: "eq",
  left: true,
  right: true,
})

/** Проверяет, является ли условие match-all. */
export const isMatchAll = (v: Userbot.ConditionExpr | null): boolean =>
  !!v && v.op === "eq" && v.left === true && v.right === true

/** Проверяет regex-паттерн на запрещённые конструкции (TZ §2.2). */
const lintRegex = (
  pattern: string,
  flags: string | undefined,
  limits: UserbotCatalog.CatalogLimits,
): string[] => {
  const errs: string[] = []
  if (pattern.length > limits.maxRegexPatternLength) {
    errs.push(
      `regex pattern length ${pattern.length} > ${limits.maxRegexPatternLength}`,
    )
  }
  // Backreferences: \1-\9 или \k<name>
  if (/\\[1-9]/.test(pattern) || /\\k<[^>]+>/.test(pattern)) {
    errs.push("regex pattern contains backreference")
  }
  // Lookbehind: (?<=...) или (?<!...)
  if (/\(\?<[=!]/.test(pattern)) {
    errs.push("regex pattern contains lookbehind")
  }
  // Flags: только imsu (без g/y)
  if (flags && /[^imsu]/.test(flags)) {
    errs.push(`regex flags "${flags}" — only imsu allowed`)
  }
  return errs
}

/**
 * Валидирует ConditionExpr. Возвращает массив сообщений об ошибках (пустой = валидно).
 * @param v — условие
 * @param limits — лимиты из catalog.limits (НЕ хардкодить)
 * @param depth — текущая глубина вложенности (для рекурсии and/or/not)
 */
export const validateConditionExpr = (
  v: Userbot.ConditionExpr | null | undefined,
  limits: UserbotCatalog.CatalogLimits | undefined,
  depth = 0,
): string[] => {
  if (!v) return ["condition is required"]
  const maxDepth = limits?.maxConditionDepth ?? Infinity
  const errs: string[] = []

  switch (v.op) {
    case "eq":
    case "ne":
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      if (v.left === undefined) errs.push("comparison: left is required")
      if (v.right === undefined) errs.push("comparison: right is required")
      break

    case "in":
    case "nin":
      if (v.left === undefined) errs.push("membership: left is required")
      if (v.right === undefined) errs.push("membership: right is required")
      break

    case "exists":
    case "empty":
      if (v.value === undefined) errs.push("unary: value is required")
      break

    case "regex": {
      if (v.value === undefined) errs.push("regex: value is required")
      if (!v.pattern) {
        errs.push("regex: pattern is required")
      } else if (limits) {
        errs.push(...lintRegex(v.pattern, v.flags, limits))
      }
      break
    }

    case "and":
    case "or": {
      if (!v.conditions || !v.conditions.length) {
        errs.push(`${v.op}: conditions array is required and non-empty`)
      }
      if (depth + 1 >= maxDepth) {
        errs.push(
          `${v.op}: depth ${depth + 1} exceeds maxConditionDepth ${maxDepth}`,
        )
      }
      if (v.conditions) {
        for (const sub of v.conditions) {
          errs.push(...validateConditionExpr(sub, limits, depth + 1))
        }
      }
      break
    }

    case "not": {
      if (!v.condition) {
        errs.push("not: condition is required")
      }
      if (depth + 1 >= maxDepth) {
        errs.push(
          `not: depth ${depth + 1} exceeds maxConditionDepth ${maxDepth}`,
        )
      }
      if (v.condition) {
        errs.push(...validateConditionExpr(v.condition, limits, depth + 1))
      }
      break
    }

    default:
      errs.push(`unknown operator: ${(v as { op: string }).op}`)
  }

  return errs
}

/** Форматирует значение для человекочитаемой сводки. */
const fmt = (v: unknown): string => {
  if (v === undefined) return "undefined"
  if (v === null) return "null"
  if (typeof v === "string") return v
  if (typeof v === "boolean" || typeof v === "number") return String(v)
  if (Array.isArray(v)) return `[${v.map(fmt).join(", ")}]`
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

/** Человекочитаемая сводка условия для таблиц/деталей. */
export const summarizeCondition = (v: Userbot.ConditionExpr | null): string => {
  if (!v) return "—"
  if (isMatchAll(v)) return "совпадает со всеми"

  switch (v.op) {
    case "eq":
      return `${fmt(v.left)} == ${fmt(v.right)}`
    case "ne":
      return `${fmt(v.left)} != ${fmt(v.right)}`
    case "gt":
      return `${fmt(v.left)} > ${fmt(v.right)}`
    case "gte":
      return `${fmt(v.left)} >= ${fmt(v.right)}`
    case "lt":
      return `${fmt(v.left)} < ${fmt(v.right)}`
    case "lte":
      return `${fmt(v.left)} <= ${fmt(v.right)}`
    case "in":
      return `${fmt(v.left)} in ${fmt(v.right)}`
    case "nin":
      return `${fmt(v.left)} not in ${fmt(v.right)}`
    case "exists":
      return `exists(${fmt(v.value)})`
    case "empty":
      return `empty(${fmt(v.value)})`
    case "regex":
      return `${fmt(v.value)} =~ /${v.pattern}/${v.flags ?? ""}`
    case "and":
      return v.conditions.map(summarizeCondition).join(" AND ")
    case "or":
      return v.conditions.map(summarizeCondition).join(" OR ")
    case "not":
      return `NOT (${summarizeCondition(v.condition)})`
    default:
      return `op:${(v as { op: string }).op}`
  }
}
